import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Server, RefreshCw, CheckCircle2, XCircle, AlertCircle,
  Database, Search, Network, HardDrive, Radio, Cloud,
  Key, ExternalLink, Zap, Shield
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_META = {
  "BGE-M3":        { icon: Zap,       desc: "Dense embeddings (1024d) for semantic search" },
  "Reranker":      { icon: Shield,    desc: "Cross-encoder reranking for precision" },
  "MiniLM":        { icon: Zap,       desc: "Fast embeddings (384d) for email KB" },
  "spaCy":         { icon: Search,    desc: "NER and text processing pipeline" },
  "Qdrant":        { icon: Database,  desc: "Vector database for similarity search" },
  "Elasticsearch": { icon: Search,    desc: "BM25 keyword search engine" },
  "Neo4j":         { icon: Network,   desc: "Graph database for document relationships" },
  "Redis":         { icon: HardDrive, desc: "Cache layer for fast responses" },
  "Kafka":         { icon: Radio,     desc: "Event streaming for async processing" },
  "MinIO":         { icon: Cloud,     desc: "S3-compatible object storage" },
};

export default function RAGInfrastructureSettings() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(null);
  const [health, setHealth] = useState(null);
  const [setupRunning, setSetupRunning] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      const [statusRes, healthRes] = await Promise.all([
        axios.get(`${API}/admin/rag-infrastructure/tensornex-status`, { headers }).catch(() => null),
        axios.get(`${API}/admin/rag-infrastructure/health`, { headers }).catch(() => null),
      ]);
      if (statusRes) setStatus(statusRes.data);
      if (healthRes) setHealth(healthRes.data);
    } catch (e) {
      console.error("Failed to fetch infrastructure status", e);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    // Clear cached health so it re-fetches from Tensornex
    await fetchAll();
    setRefreshing(false);
    toast.success("Infrastructure status refreshed");
  };

  const handleSetup = async () => {
    setSetupRunning(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/admin/rag-infrastructure/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Infrastructure setup complete");
      await fetchAll();
    } catch (e) {
      toast.error("Setup failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSetupRunning(false);
    }
  };

  const hasKey = status?.has_api_key;
  const errorType = status?.error_type;
  const isHealthy = status?.healthy;
  const services = status?.services || {};

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="rag-infrastructure-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">RAG Infrastructure</h1>
          <p className="text-sm text-slate-500 mt-1">
            All services powered by{" "}
            <a href="https://tensornex.cloud" target="_blank" rel="noopener noreferrer"
               className="text-cyan-600 hover:underline font-medium">
              Tensornex GPU Platform
            </a>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}
                  data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={handleSetup} disabled={setupRunning || !hasKey}
                  className="bg-cyan-600 hover:bg-cyan-700 text-white"
                  data-testid="setup-btn">
            <Server className="w-4 h-4 mr-1.5" />
            {setupRunning ? "Setting up..." : "Setup Collections"}
          </Button>
        </div>
      </div>

      {/* Status Alerts */}
      {loading ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center text-slate-400">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading infrastructure status...
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Alert: No API Key */}
          {!hasKey && (
            <Alert className="border-red-300 bg-red-50" data-testid="no-key-alert">
              <XCircle className="w-5 h-5 text-red-600" />
              <AlertTitle className="text-red-800 font-semibold">API Key Not Configured</AlertTitle>
              <AlertDescription className="text-red-700">
                No Tensornex API key found. Go to{" "}
                <a href="/settings/api-keys" className="underline font-medium">Settings &gt; API Keys</a>{" "}
                and enter your <code className="bg-red-100 px-1 rounded text-xs">gs_live_...</code> key
                in the Tensornex section.
              </AlertDescription>
            </Alert>
          )}

          {/* Alert: Invalid Key */}
          {hasKey && errorType === "invalid_api_key" && (
            <Alert className="border-red-300 bg-red-50" data-testid="invalid-key-alert">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <AlertTitle className="text-red-800 font-semibold">Invalid API Key</AlertTitle>
              <AlertDescription className="text-red-700">
                Your Tensornex API key is invalid or expired.{" "}
                <a href="/settings/api-keys" className="underline font-medium">Update it here</a>.
              </AlertDescription>
            </Alert>
          )}

          {/* Alert: Rate Limited */}
          {errorType === "rate_limited" && (
            <Alert className="border-amber-300 bg-amber-50" data-testid="rate-limit-alert">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <AlertTitle className="text-amber-800 font-semibold">Rate Limited</AlertTitle>
              <AlertDescription className="text-amber-700">
                Tensornex API is temporarily rate limiting requests. Calls will automatically retry with backoff.
                Consider upgrading your plan at{" "}
                <a href="https://tensornex.cloud" target="_blank" rel="noopener noreferrer"
                   className="underline font-medium">tensornex.cloud</a>.
              </AlertDescription>
            </Alert>
          )}

          {/* Alert: Connection Error */}
          {hasKey && errorType === "connection_error" && (
            <Alert className="border-red-300 bg-red-50" data-testid="connection-error-alert">
              <XCircle className="w-5 h-5 text-red-600" />
              <AlertTitle className="text-red-800 font-semibold">Platform Unreachable</AlertTitle>
              <AlertDescription className="text-red-700">
                {status?.message || "Cannot reach Tensornex platform."}{" "}
                RAG V4 actors have auto-fallen back to Basic mode.
              </AlertDescription>
            </Alert>
          )}

          {/* Connected Banner */}
          {hasKey && isHealthy && !errorType && (
            <Alert className="border-emerald-300 bg-emerald-50" data-testid="connected-alert">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <AlertTitle className="text-emerald-800 font-semibold">Connected via Tensornex</AlertTitle>
              <AlertDescription className="text-emerald-700">
                All infrastructure services are running on Tensornex GPU Platform.
                One API key powers everything — no individual service credentials needed.
              </AlertDescription>
            </Alert>
          )}

          {/* Service Grid */}
          <Card className="border-slate-200 shadow-sm" data-testid="services-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Server className="w-5 h-5 text-slate-600" />
                Service Status
                {hasKey && (
                  <Badge variant={isHealthy ? "default" : "destructive"}
                         className={isHealthy
                           ? "bg-emerald-100 text-emerald-700 ml-auto"
                           : "bg-red-100 text-red-700 ml-auto"}
                         data-testid="overall-badge">
                    {isHealthy ? "All Healthy" : errorType === "no_api_key" ? "Not Configured" : "Issues Detected"}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                10 GPU-accelerated services managed by a single Tensornex API key
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="service-grid">
                {Object.entries(SERVICE_META).map(([name, meta]) => {
                  const ok = services[name];
                  const Icon = meta.icon;
                  const unknown = !hasKey || Object.keys(services).length === 0;

                  return (
                    <div key={name}
                         className={`flex flex-col items-center p-3 rounded-lg border text-center transition-colors ${
                           unknown ? "border-slate-200 bg-slate-50" :
                           ok ? "border-emerald-200 bg-emerald-50" :
                           "border-red-200 bg-red-50"
                         }`}
                         data-testid={`service-${name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}>
                      <div className={`p-2 rounded-full mb-1.5 ${
                        unknown ? "bg-slate-200" : ok ? "bg-emerald-200" : "bg-red-200"
                      }`}>
                        <Icon className={`w-4 h-4 ${
                          unknown ? "text-slate-500" : ok ? "text-emerald-700" : "text-red-700"
                        }`} />
                      </div>
                      <span className={`text-xs font-semibold ${
                        unknown ? "text-slate-600" : ok ? "text-emerald-800" : "text-red-800"
                      }`}>{name}</span>
                      <span className="text-[10px] text-slate-500 mt-0.5 leading-tight">{meta.desc}</span>
                      <div className="mt-1">
                        {unknown ? (
                          <span className="text-[10px] text-slate-400">—</span>
                        ) : ok ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        ) : (
                          <XCircle className="w-3.5 h-3.5 text-red-600" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* MongoDB (always local) */}
          {health && (
            <Card className="border-slate-200 shadow-sm" data-testid="mongodb-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Database className="w-5 h-5 text-slate-600" />
                  MongoDB
                  {health.services?.find(s => s.service === "mongodb")?.healthy ? (
                    <Badge className="bg-emerald-100 text-emerald-700 ml-auto">Connected</Badge>
                  ) : (
                    <Badge variant="destructive" className="bg-red-100 text-red-700 ml-auto">Error</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Local database for application data — not managed by Tensornex
                </CardDescription>
              </CardHeader>
            </Card>
          )}

          {/* Quick Links */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-3">
                <a href="/settings/api-keys" className="flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 font-medium">
                  <Key className="w-4 h-4" /> Manage API Keys
                </a>
                <a href="/settings/rag-config" className="flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 font-medium">
                  <Server className="w-4 h-4" /> RAG Config
                </a>
                <a href="/settings/cdm-documents" className="flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 font-medium">
                  <Database className="w-4 h-4" /> CDM Documents
                </a>
                <a href="https://tensornex.cloud" target="_blank" rel="noopener noreferrer"
                   className="flex items-center gap-1.5 text-sm text-cyan-600 hover:text-cyan-800 font-medium">
                  <ExternalLink className="w-4 h-4" /> Tensornex Dashboard
                </a>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
